import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RoleBadge } from './RoleBadge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Info } from 'lucide-react';

interface RoleSelectorProps {
  selectedRoles: string[] | null | undefined;
  onChange: (roles: string[] | null) => void;
  label?: string;
  description?: string;
  className?: string;
  disabled?: boolean;
}

const AVAILABLE_ROLES = ['operator', 'quality', 'site_manager'];

export function RoleSelector({
  selectedRoles,
  onChange,
  label,
  description,
  className,
  disabled = false,
}: RoleSelectorProps) {
  const { t } = useTranslation();

  const handleRoleToggle = (role: string, checked: boolean) => {
    const currentRoles = selectedRoles || [];
    let newRoles: string[];

    if (checked) {
      newRoles = [...currentRoles, role];
    } else {
      newRoles = currentRoles.filter((r) => r !== role);
    }

    // If no roles selected, set to null (means any role can access)
    onChange(newRoles.length > 0 ? newRoles : null);
  };

  const isRoleSelected = (role: string) => {
    return selectedRoles?.includes(role) || false;
  };

  const hasNoRoles = !selectedRoles || selectedRoles.length === 0;

  return (
    <div className={cn('space-y-3', className)}>
      {label && (
        <div className="space-y-1">
          <Label className="text-base font-semibold">{label}</Label>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      )}

      <div className="space-y-2">
        {AVAILABLE_ROLES.map((role) => (
          <Card
            key={role}
            className={cn(
              'p-3 transition-all cursor-pointer hover:shadow-md',
              isRoleSelected(role) && 'border-primary border-2 bg-primary/5',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            onClick={() => {
              if (!disabled) {
                handleRoleToggle(role, !isRoleSelected(role));
              }
            }}
          >
            <div className="flex items-center space-x-3">
              <Checkbox
                id={`role-${role}`}
                checked={isRoleSelected(role)}
                onCheckedChange={(checked) =>
                  handleRoleToggle(role, checked as boolean)
                }
                disabled={disabled}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="flex-1 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <RoleBadge role={role} size="md" />
                  <span className="text-sm text-muted-foreground">
                    {t(`roles.${role}.description`)}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {hasNoRoles && (
        <Card className="p-3 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-900 dark:text-blue-100">
              {t('roles.noRolesSelected')}
            </p>
          </div>
        </Card>
      )}

      {!hasNoRoles && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="w-4 h-4" />
          <span>{t('roles.selectedCount', { count: selectedRoles?.length || 0 })}</span>
        </div>
      )}
    </div>
  );
}
